import axios from 'axios'
import { PROVINCE_OPTIONS } from '@myap/metadata'
import get from 'lodash-es/get'
import set from 'lodash-es/set'
import { formatDate, DATETIME_FORMATS } from '@myap/ui-library/esm/date'
import { reduxForm, getFormValues, change, submit, isInvalid, reset } from 'redux-form'
import { submitGenerateAPID } from '../../actions/generateAPID'
import { Error, Text, Select, Input, Button } from '../../components/common'
import { CheckDateFormatIfNotEmpty, ZipValidation } from '../../components/common/forms/Validations'
import { formatZip } from '../../utils/numbers'
import { getGenerateAPIDStatusProps } from '../../selectors/generateAPID'
import {
  ADDRESS_DOMESTIC,
  ADDRESS_INTERNATIONAL,
  GENERATE_APID_FORM_NAME,
  GENERATE_APID_FORM_FIELDS as FIELDS,
} from '../../constants/GenerateAPIDConstants'
import {
  getCurrentEducationPeriodCd,
  getPreviousEducationPeriodCd,
  getEducationPeriods,
} from '../../selectors/settings'
import { sortColumnByKey } from '../../utils/sort'

const COUNTRY_OPTIONS = APRICOT.CBData.countries.map(country => ({
  label: country.name,
  value: country.iso.toUpperCase(),
}))
const STATE_OPTIONS = APRICOT.CBData.states.map(state => ({
  label: state.name,
  value: state.value,
}))

const mapStateToProps = state => {
  const currentEdPd = getCurrentEducationPeriodCd(state)
  const previousEdPd = getPreviousEducationPeriodCd(state)
  const edpds = getEducationPeriods(state)
  const edpdOptions = [
    { value: currentEdPd, label: edpds[currentEdPd]?.descr },
    { value: previousEdPd, label: edpds[previousEdPd]?.descr },
  ]
  const generateAPIDFormValues = getFormValues(GENERATE_APID_FORM_NAME)(state)
  const { submitting, submitted } = getGenerateAPIDStatusProps(state)
  const initialValues = {
    [FIELDS.edpdCd]: '',
    [FIELDS.examCd]: '',
    [FIELDS.aiCode]: '',
    [FIELDS.testWindow]: [],
    [FIELDS.serialNum]: '',
    [FIELDS.firstName]: '',
    [FIELDS.lastName]: '',
    [FIELDS.dob]: '',
  }
  set(initialValues, FIELDS.addrType, ADDRESS_DOMESTIC)
  set(initialValues, FIELDS.addr1, '')
  set(initialValues, FIELDS.addr2, '')
  set(initialValues, FIELDS.addr3, '')
  set(initialValues, FIELDS.city, '')
  set(initialValues, FIELDS.state, '')
  set(initialValues, FIELDS.province, '')
  set(initialValues, FIELDS.zip5, '')
  set(initialValues, FIELDS.zip4, '')
  set(initialValues, FIELDS.postalCd, '')
  set(initialValues, FIELDS.country, '')
  set(initialValues, FIELDS.region, '')
  return {
    submitting,
    submitted,
    isInvalid: isInvalid(GENERATE_APID_FORM_NAME)(state),
    initialValues,
    currentFormValues: generateAPIDFormValues || initialValues,
    edpdOptions,
  }
}

const serialNumberValidation = value =>
  /[0-9]{7}\/[0-9]{4}/.test(value) ? undefined : 'Must be in the format 1234567/1234'

const ProvinceField = connect(null, { change })(({ countryCode, change }) => {
  const hasProvinceOptions = Object.keys(PROVINCE_OPTIONS).some(p => p === countryCode)

  useEffect(() => {
    change(GENERATE_APID_FORM_NAME, FIELDS.province, '')
  }, [countryCode])

  if (hasProvinceOptions) {
    return (
      <Select
        name={FIELDS.province}
        label="Province"
        isRequired={true}
        options={PROVINCE_OPTIONS[countryCode]}
        parse={val => parseInt(val, 10)}
        onChange={e =>
          change(GENERATE_APID_FORM_NAME, FIELDS.province, parseInt(e.target.value, 10))
        }
      />
    )
  }

  return (
    <Text
      name={FIELDS.province}
      label="Province"
      isRequired={true}
      onChange={e => change(GENERATE_APID_FORM_NAME, FIELDS.province, e.target.value)}
    />
  )
})

const GenerateAPIDForm = ({
  currentFormValues,
  edpdOptions,
  submitting,
  error,
  isInvalid,
  handleSubmit,
  change,
}) => {
  const [etsTestWindowOptions, setEtsTestWindowOptions] = useState([])
  useEffect(() => {
    async function fetchEtsExamWindows(edperiod) {
      const examWindowDataForCourses = []
      const uniqueEtsExamWindows = new Set()
      const { data } = await axios.get(
        `${Config.API_URL}/paapro/v1/exam-windows/education-periods/${edperiod}`
      )
      Object.values(data)
        .flatMap(visibleWindows => visibleWindows)
        .forEach(window => {
          if (!uniqueEtsExamWindows.has(window.etsTestWindowLabel)) {
            uniqueEtsExamWindows.add(window.etsTestWindowLabel)
            examWindowDataForCourses.push({
              value: window.etsTestWindowLabel,
              label: window.etsTestWindowLabel,
              sequence: window.sequence,
            })
          }
        })
      const sortedExamWindowDataForCourses = sortColumnByKey(
        Object.values(examWindowDataForCourses),
        ['sequence'],
        ['asc']
      )
      setEtsTestWindowOptions(sortedExamWindowDataForCourses)
    }
    if (currentFormValues?.[FIELDS.edpdCd]) {
      fetchEtsExamWindows(currentFormValues[FIELDS.edpdCd])
    }
  }, [currentFormValues?.[FIELDS.edpdCd]])

  return (
    <form onSubmit={handleSubmit}>
      <fieldset>
        <Select
          name={FIELDS.edpdCd}
          label="Academic Year"
          isRequired={true}
          showEmptyOption={true}
          options={edpdOptions}
          placeholder="Choose"
          normalize={val => parseInt(val, 10)}
        />
        <Text
          name={FIELDS.aiCode}
          label="Testing AI"
          placeholder="Enter valid Testing AI"
          isRequired={true}
        />
        <Text
          name={FIELDS.serialNum}
          label="Batch/Serial number"
          placeholder="Enter Batch/Serial number"
          isRequired={true}
          validate={[serialNumberValidation]}
        />
        <Text
          name={FIELDS.examCd}
          label="ETS Exam Code"
          placeholder="Enter ETS Exam Code"
          isRequired={true}
        />
        <Select
          name={FIELDS.testWindow}
          label="Exam Administration"
          isRequired={true}
          showEmptyOption={true}
          options={etsTestWindowOptions}
        />
        <Text name={FIELDS.firstName} label="Student first name" />
        <Text name={FIELDS.lastName} label="Student last name" />
        <Text
          name={FIELDS.dob}
          label="DOB (YYYY-MM-DD)"
          placeholder="Enter DOB"
          isRequired={false}
          validate={[CheckDateFormatIfNotEmpty]}
        />
      </fieldset>
      <fieldset>
        <legend>Mailing Address</legend>
        <Input
          type="radio"
          name={FIELDS.addrType}
          value="DOMESTIC"
          checked={get(currentFormValues, FIELDS.addrType) === ADDRESS_DOMESTIC}
          label="Domestic"
          style={{ display: 'inline-block', marginRight: '1em', marginBottom: '24px' }}
        />
        <Input
          type="radio"
          name={FIELDS.addrType}
          value="INTERNATIONAL"
          checked={get(currentFormValues, FIELDS.addrType) === ADDRESS_INTERNATIONAL}
          label="International"
          style={{ display: 'inline-block', marginBottom: '24px' }}
        />
        <Text name={FIELDS.addr1} label="Address 1" isRequired={true} />
        <Text name={FIELDS.addr2} label="Address 2" />
        <Text name={FIELDS.addr3} label="Address 3" />
        <Text name={FIELDS.city} label="City" isRequired={true} />
        {get(currentFormValues, FIELDS.addrType) === ADDRESS_DOMESTIC ? (
          <>
            <Select
              name={FIELDS.state}
              label="State"
              isRequired={true}
              options={STATE_OPTIONS}
              showEmptyOption={false}
            />
            <Text
              name={FIELDS.zip5}
              label="Zip"
              placeholder="Enter 5 or 9 digit code"
              isRequired={true}
              maxlength={9}
              format={formatZip}
              validate={[ZipValidation]}
            />
          </>
        ) : (
          <>
            <Select
              name={FIELDS.country}
              label="Country"
              isRequired={true}
              options={COUNTRY_OPTIONS}
            />
            <ProvinceField
              currentFormValues={currentFormValues}
              countryCode={get(currentFormValues, FIELDS.country)}
            />
            <Text name={FIELDS.postalCd} label="Postal Code" isRequired={true} />
          </>
        )}
      </fieldset>
      <Button
        isSubmit={true}
        isPrimary={true}
        isDisabled={isInvalid || submitting}
        busy={submitting}
      >
        Generate
      </Button>
      {error && <Error message={error} />}
    </form>
  )
}

export default connect(mapStateToProps, {
  submitGenerateAPID,
  change,
  submit,
  reset,
})(
  reduxForm({
    form: GENERATE_APID_FORM_NAME,
    onSubmit: async (values, dispatch, props) => {
      const valuesForSubmit = { ...values }
      valuesForSubmit[FIELDS.dob] = formatDate(
        valuesForSubmit[FIELDS.dob],
        DATETIME_FORMATS.shortMonthDayYear
      )
      if (get(valuesForSubmit, FIELDS.addrType) === ADDRESS_DOMESTIC) {
        set(valuesForSubmit, FIELDS.country, 'US')
        // Blank out international address fields
        set(valuesForSubmit, FIELDS.province, '')
        set(valuesForSubmit, FIELDS.postalCd, '')
        set(valuesForSubmit, FIELDS.region, '')
        const zips = get(valuesForSubmit, FIELDS.zip5).split('-')
        if (zips.length === 2) {
          set(valuesForSubmit, FIELDS.zip5, zips[0])
          set(valuesForSubmit, FIELDS.zip4, zips[1])
        }
      } else {
        // Blank out US Address fields
        set(valuesForSubmit, FIELDS.state, '')
        set(valuesForSubmit, FIELDS.zip5, '')
        set(valuesForSubmit, FIELDS.zip4, '')
      }
      return props.submitGenerateAPID(valuesForSubmit)
    },
    onSubmitSuccess: (result, dispatch, props) => {
      props.reset(GENERATE_APID_FORM_NAME)
      // Display table of generated AP IDs?
    },
  })(GenerateAPIDForm)
)
